import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Button } from 'Components';
import Pill from 'Components/Pill';
import { NotificationManager } from 'Components/Notifications';
import { TypeLayer } from 'Components/Map/providers/enums';

import {
  mapLandmarksOpenDeleteModal,
  mapLandmarksDeleteLandmarks,
  addUpdateLandmark,
  clearRedux
} from 'Redux/actions';

import './style.scss';

const LandmarkDelete = (props) => {
  const dispatch = useDispatch();
  const { intl, mapRef } = props;
  const { messages } = intl;

  const {
    open,
    landmarks,
    response,
    loadingResponse,
    deleteAll,
    unselectedRows,
    filterTags
  } = useSelector((state) => state.realtimeMapLandmarkDeleteModuleRedux);

  useEffect(() => {
    return () => {
      dispatch(clearRedux("MAP_LANDMARKS_DELETE_LANDMARK"));
    }
  }, []);

  useEffect(() => {
    if (response && open) {
      if (response.error) {
        NotificationManager.error(
          response.message || messages['errorMessage'],
          messages['error'],
          3000,
          null,
          null,
          'warning',
        );
        dispatch(clearRedux("MAP_LANDMARKS_DELETE_LANDMARK", { response: null }));
      } else {
        NotificationManager.success(
          landmarks && landmarks.length > 1 ? messages['mapLandmarkDeleteLandmark_success'] :  messages['mapLandmarkDeleteLandmark_success_one'],
          messages['success'],
          3000,
          null,
          null,
          'success',
        );

        dispatch(addUpdateLandmark("remove", [...landmarks]));
        if(mapRef?.current){
          mapRef.current.removeLayer(TypeLayer.TemporalObjects);
          mapRef.current.hideMarkerModal();
        }
        handleClose();
      }
    }
  }, [response, open]);

  const handleClose = useCallback(() => {
    dispatch(mapLandmarksOpenDeleteModal(false));
    dispatch(clearRedux("MAP_LANDMARKS_DELETE_LANDMARK"));
  });

  const AllLandmarks = React.createElement(
    'a',
    {
      className: 'landmark-delete-all'
    },
    messages['mapLandmarkDeleteLandmark_remove_all_landmark_keyword']
  );

  const confirmationMessage = unselectedRows && unselectedRows.length > 0
    ? messages['mapLandmarkDeleteLandmark_remove_all_except']
    : '';

  // When a tag filter is active, classify each tag:
  // - No deselected rows belonging to it → full tag pill
  // - Some deselected rows belonging to it → show individual selected landmarks instead
  const getFilterTagsDisplay = () => {
    if (!filterTags || filterTags.length === 0) return { tagPills: [], individualLandmarks: [] };

    if (!unselectedRows || unselectedRows.length === 0) {
      return { tagPills: filterTags, individualLandmarks: [] };
    }

    const tagPills = [];
    const individualLandmarks = [];

    filterTags.forEach((tag) => {
      const hasUnselected = unselectedRows.some((row) => {
        if (!row.tagsId) return false;
        return row.tagsId.split(',').includes(tag.id.toString());
      });

      if (!hasUnselected) {
        tagPills.push(tag);
      } else {
        const tagLandmarks = (landmarks || []).filter((l) => {
          if (!l.tagsId) return false;
          return l.tagsId.split(',').includes(tag.id.toString());
        });
        individualLandmarks.push(...tagLandmarks);
      }
    });

    // De-duplicate landmarks that belong to multiple selected tags
    const seen = new Set();
    const dedupedLandmarks = individualLandmarks.filter((l) => {
      if (seen.has(l.id)) return false;
      seen.add(l.id);
      return true;
    });

    return { tagPills, individualLandmarks: dedupedLandmarks };
  };

  const { tagPills, individualLandmarks } = getFilterTagsDisplay();

  return (
    <Modal
      className="modal-top-0"
      handleClose={handleClose}
      headerStyle={{ top: 0 }}
      loading={loadingResponse}
      open={open}
      position="right"
      size="extra-small"
      title={messages["mapLandmarkDeleteLandmark_title"]}
    >
      <ModalBody>
        <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>

          <div>
            {
              filterTags && filterTags.length > 0
              ?
              <p className='landmark-delete-message'>
                {messages['mapLandmarkDeleteLandmark_remove_filtered_tags']}
              </p>
              :
              deleteAll
              ?
              <p className='landmark-delete-message'>
                {messages['mapLandmarkDeleteLandmark_remove_all']} {AllLandmarks} {` ${confirmationMessage}?`}
              </p>
              :
              <p>
                {
                  (landmarks && landmarks.length > 1) 
                  ? 
                  messages["mapLandmarkDeleteLandmark_detail"] 
                  : 
                  messages["mapLandmarkDeleteLandmark_detail_one"]
                }
              </p>
            }
            <p>
              {messages["mapLandmarkDeleteLandmark_warning"]}
            </p>
          </div>

          <div style={{ padding: 10 }}>
            {
              filterTags && filterTags.length > 0
              ?
              <div className="landmark-delete-filter-tags">
                {
                  tagPills.map((tag, index) => (
                    <Pill
                      key={index}
                      item={tag}
                      textField="name"
                      colorField="color"
                      clickable={false}
                      style={{ margin: '4px 4px 4px 0' }}
                    />
                  ))
                }
                {
                  individualLandmarks.length > 0 && (
                    <ul style={{ width: '100%', margin: '8px 0 0 0', paddingLeft: 20 }}>
                      {individualLandmarks.map((landmark, index) => (
                        <li key={index}>
                          <span>{landmark.name}</span>
                        </li>
                      ))}
                    </ul>
                  )
                }
              </div>
              :
              deleteAll ?
                <ul>
                  {
                    unselectedRows
                    &&
                    unselectedRows.map((landmark, index) => {
                      return (
                        <li key={index}>
                          <span>{landmark.name}</span>
                        </li>
                      )
                    })
                  }
                </ul>
              :
              (
              <ul>
                {
                  landmarks
                  &&
                  landmarks.map((landmark, index) => {
                    return (
                      <li key={index}>
                        <span>{landmark.name}</span>
                      </li>
                    )
                  })
                }
              </ul>
              )
            }
            
          </div>

        </div>
      </ModalBody>

      <ModalFooter style={{ bottom: 50 }}>
        <Button
          disabled={loadingResponse}
          onClick={handleClose}
          className="btn-cancel"
        >
          {messages['cancel']}
        </Button>

        <Button
          disabled={loadingResponse}
          onClick={(e) => {
            if (e) e.preventDefault();
            dispatch(mapLandmarksDeleteLandmarks(landmarks, deleteAll, unselectedRows));
          }}
          className="btn-red"
        >
          {messages['delete']}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default injectIntl(LandmarkDelete);